import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { login as accountLogin, logout as accountLogout, authorize, forgotPassword as accountForgotPassword, resetPassword as accountResetPassword, changePassword as accountChangePassword } from '../services/accountService';

const CognitoAuthContext = createContext();

export const useAuth = () => {
  const context = useContext(CognitoAuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a CognitoAuthProvider');
  }
  return context;
};

export const CognitoAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [idToken, setIdToken] = useState(() => {
    // Try to get ID token from localStorage on initialization
    try {
      return localStorage.getItem('idToken');
    } catch (error) {
      console.warn('Failed to get ID token from localStorage:', error);
      return null;
    }
  });
  
  // Flag to prevent multiple concurrent auth checks
  const isCheckingAuthRef = useRef(false);

  const checkAuthStatus = useCallback(async () => {
    // Prevent multiple concurrent auth checks
    if (isCheckingAuthRef.current) {
      return;
    }
    
    try {
      isCheckingAuthRef.current = true;
      setIsLoading(true);
      
      // Use account-service for authentication
      try {
        const authResult = await authorize();
        if (authResult && authResult.user) {
          const { user, IdToken } = authResult;
          
          // Store the ID token for lambda authentication
          if (IdToken) {
            setIdToken(IdToken);
          }
          
          // Extract role from Cognito user object
          let role = 'user';
          let email = user.Username;
          let clientId = null;
          let department = null;
          
          if (user.UserAttributes) {
            // Find email attribute
            const emailAttr = user.UserAttributes.find(attr => attr.Name === 'email');
            if (emailAttr) {
              email = emailAttr.Value;
            }
            
            // Find user type/role attribute
            const userTypeAttr = user.UserAttributes.find(attr => attr.Name === 'custom:user_type');
            if (userTypeAttr) {
              role = userTypeAttr.Value;
            }
            
            // Find client ID attribute
            const clientIdAttr = user.UserAttributes.find(attr => attr.Name === 'custom:client_id');
            if (clientIdAttr) {
              clientId = clientIdAttr.Value;
            }
            
            // Find department attribute
            const deptAttr = user.UserAttributes.find(attr => attr.Name === 'custom:department');
            if (deptAttr) {
              department = deptAttr.Value;
            }
          }
          
          const userInfo = {
            username: user.Username,
            email: email,
            groups: user.groups || [],
            role: role,
            clientId: clientId,
            department: department,
            sub: user.Username,
          };
          
          setUser(userInfo);
          setIsAuthenticated(true);
          setError(null);
          return;
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // Only clear authentication state if user is not already authenticated
        // This prevents clearing valid sessions when /auth/me temporarily fails
        setUser(null);
        setIsAuthenticated(false);
        setIdToken(null);
        setError(null);
        // Clear ID token from localStorage
        localStorage.removeItem('idToken');
        return;
      }
      
      // No valid auth result, clear everything
      setUser(null);
      setIsAuthenticated(false);
      setIdToken(null);
      setError(null);
      // Clear tokens from localStorage
      localStorage.removeItem('idToken');
      localStorage.removeItem('accessToken');
    } catch (error) {
      console.error('Auth status check failed:', error);
      // Clear authentication state on any error
      setUser(null);
      setIsAuthenticated(false);
      setIdToken(null);
      setError(null);
      // Clear tokens from localStorage
      localStorage.removeItem('idToken');
      localStorage.removeItem('accessToken');
    } finally {
      setIsLoading(false);
      isCheckingAuthRef.current = false;
    }
  }, []); // Remove isCheckingAuth dependency to prevent infinite loop

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const signInUser = async (username, password, newPassword = null) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Use account-service for authentication
      
      try {
        const result = await accountLogin(username, password, newPassword);
        
        // Check if NEW_PASSWORD_REQUIRED challenge is returned
        if (result?.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
          return {
            isSignedIn: false,
            challengeName: result.ChallengeName,
            session: result.Session,
            message: result.message,
          };
        }
        
        // Store the ID token and set user state directly from login result
        if (result && result.AuthenticationResult && result.AuthenticationResult.IdToken) {
          const token = result.AuthenticationResult.IdToken;
          setIdToken(token);
          // Store in localStorage for persistence across page refreshes
          localStorage.setItem('idToken', token);
        }
        
        // Set user state from the login result
        if (result && result.user) {
          const user = result.user;
          
          // Extract role from user object
          let role = 'user';
          let email = user.email || user.Username;
          let clientId = null;
          let department = null;
          
          // Get user type from custom attribute
          if (user['custom:user_type']) {
            role = user['custom:user_type'];
          }
          
          // Get client ID if available
          if (user['custom:client_id']) {
            clientId = user['custom:client_id'];
          }
          
          // Get department if available
          if (user['custom:department']) {
            department = user['custom:department'];
          }
          
          const userData = {
            username: user.Username || user.sub,
            email: email,
            role: role,
            clientId: clientId,
            department: department,
            sub: user.sub,
            attributes: user
          };
          
          setUser(userData);
          setIsAuthenticated(true);
          setError(null);
        }
        
        return { isSignedIn: true };
      } catch (error) {
        setError(error.message || 'Login failed');
        throw error;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOutUser = async () => {
    try {
      setIsLoading(true);
      
      // Use account-service for logout
      console.log('🚪 LOGOUT ATTEMPT - Using account-service for logout');
      
      try {
        await accountLogout();
      } catch (error) {
        console.warn('Account-service logout failed:', error);
      }
      
      setUser(null);
      setIsAuthenticated(false);
      setIdToken(null);
      setError(null);
      // Clear tokens from localStorage
      localStorage.removeItem('idToken');
      localStorage.removeItem('accessToken');
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPasswordUser = async (email) => {
    try {
      setIsLoading(true);
      setError(null);
      
      await accountForgotPassword(email);
    } catch (error) {
      setError(error.message || 'Forgot password failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPasswordUser = async (email, code, password) => {
    try {
      setIsLoading(true);
      setError(null);
      
      await accountResetPassword(email, code, password);
    } catch (error) {
      setError(error.message || 'Reset password failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const changePasswordUser = async (oldPassword, newPassword) => {
    try {
      setIsLoading(true);
      setError(null);
      
      await accountChangePassword(oldPassword, newPassword);
    } catch (error) {
      setError(error.message || 'Change password failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const hasRole = (requiredRoles) => {
    if (!user || !user.role) return false;
    
    const roleHierarchy = {
      admin: 4,
      staff: 3,
      client: 2,
      user: 1,
    };

    const userRoleLevel = roleHierarchy[user.role] || 0;
    
    return requiredRoles.some(requiredRole => {
      const requiredRoleLevel = roleHierarchy[requiredRole] || 0;
      return userRoleLevel >= requiredRoleLevel;
    });
  };

  const getIdTokenFromCookies = useCallback(async () => {
    // First try localStorage
    try {
      const storedToken = localStorage.getItem('idToken');
      if (storedToken) {
        setIdToken(storedToken);
        return storedToken;
      }
    } catch (error) {
      console.warn('Failed to get ID token from localStorage:', error);
    }
    
    console.warn('No ID token available. User needs to log in again.');
    return null;
  }, []);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    idToken,
    signIn: signInUser,
    signOut: signOutUser,
    forgotPassword: forgotPasswordUser,
    resetPassword: resetPasswordUser,
    changePassword: changePasswordUser,
    hasRole,
    checkAuthStatus,
    getIdTokenFromCookies,
  };

  return (
    <CognitoAuthContext.Provider value={value}>
      {children}
    </CognitoAuthContext.Provider>
  );
};
